/**
 * The Atlas navigation model -- ONE declaration, not eleven hand-written links.
 *
 * The sidebar, the router and the breadcrumb all read this array, so the
 * information architecture is stated once and cannot drift between them.
 *
 * `key` matches the keys the server returns in the session payload. An entry
 * renders only when the server said the caller may see it; the client never
 * decides that for itself (ADR-14).
 *
 * `deliveredBy` is what the placeholder page shows for a slot whose feature
 * has not been built yet, so an unfinished destination explains itself rather
 * than looking broken.
 *
 * Engagements deliberately have NO entry: an engagement is administered inside
 * Client detail, never from the navigation (ADR-17).
 */

import {
  Briefcase,
  Building2,
  CalendarDays,
  FileText,
  LayoutDashboard,
  Layers,
  Library,
  Network,
  ScrollText,
  Settings,
  UploadCloud,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/** Sidebar groups, in display order. `null` means ungrouped (rendered first). */
export type NavGroup =
  | 'ORGANIZATION'
  | 'COMMITMENT'
  | 'MANAGEMENT'
  | 'MONITOR'
  | null;

export interface NavEntry {
  key: string;
  label: string;
  path: string;
  icon: LucideIcon;
  group: NavGroup;
  /** Which phase or feature delivers the real screen. */
  deliveredBy: string;
  /** What the screen will do, shown on the placeholder. */
  summary: string;
}

export const NAV_ENTRIES: readonly NavEntry[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    group: null,
    deliveredBy: 'Phase 16',
    summary:
      'Counts of Business Unit Groups, Business Units, Clients and Engagements, plus current-cycle figures, every number scoped to what you are permitted to see.',
  },
  {
    key: 'artifacts',
    label: 'Artifacts',
    path: '/artifacts',
    icon: FileText,
    group: null,
    deliveredBy: 'Phase 15',
    summary:
      'Every artifact you may see across the hierarchy, what each one hangs off, and its published state.',
  },
  {
    key: 'cycles',
    label: 'Cycles',
    path: '/cycles',
    icon: CalendarDays,
    group: 'COMMITMENT',
    deliveredBy: 'f10.2',
    summary:
      'The monthly COM cycle: Finance preparation, manager forecast input, live review, freeze and reconciliation.',
  },
  {
    key: 'organization-overview',
    label: 'Overview',
    path: '/organization/overview',
    icon: Building2,
    group: 'ORGANIZATION',
    deliveredBy: 'Organization Overview',
    summary:
      'The whole Business Unit Group, Business Unit, Client and Engagement hierarchy you may see, with its owners, in four layouts: tree table, columns, card drill-down and block chart.',
  },
  {
    key: 'business-unit-groups',
    label: 'Business Unit Groups',
    path: '/business-unit-groups',
    icon: Layers,
    group: 'ORGANIZATION',
    deliveredBy: 'Phase 4',
    summary:
      'The top hierarchy level, with its DEM and CEM owners. Deactivation only; nothing is ever hard-deleted.',
  },
  {
    key: 'business-units',
    label: 'Business Units',
    path: '/business-units',
    icon: Network,
    group: 'ORGANIZATION',
    deliveredBy: 'Phase 5',
    summary:
      'The second hierarchy level and the unit of COM data entry, with its reporting currency and CM owner.',
  },
  {
    key: 'clients',
    label: 'Clients',
    path: '/clients',
    icon: Briefcase,
    group: 'ORGANIZATION',
    deliveredBy: 'Phase 6',
    summary:
      'The searchable client list, and the Client detail hub whose tabs carry Engagements, Commitments and Artifacts.',
  },
  {
    key: 'data-import',
    label: 'Data import',
    path: '/data-import',
    icon: UploadCloud,
    group: 'ORGANIZATION',
    deliveredBy: 'Phase 8',
    summary:
      'Staged upload of the master workbook: what was parsed, what will be created, and every role holder that matched no user.',
  },
  {
    key: 'budget',
    label: 'Budget',
    path: '/administration/budget',
    icon: Wallet,
    group: 'ORGANIZATION',
    deliveredBy: 'f10.10',
    summary:
      'Finance-maintained reference data: the annual budget per client and the monthly budget per Business Unit, each with an explicit currency.',
  },
  {
    key: 'audit-log',
    label: 'Audit log',
    path: '/audit-log',
    icon: ScrollText,
    group: 'MONITOR',
    deliveredBy: 'Feature f1.5',
    summary:
      'The Admin-only append-only log: every state-changing request and every authentication attempt, including the ones that were denied or failed.',
  },
  {
    key: 'users',
    label: 'Users',
    path: '/users',
    icon: Users,
    group: 'MANAGEMENT',
    deliveredBy: 'Feature f3.1',
    summary:
      'The Admin-only account roster: every user with their platform role and state, and the grant, revoke, deactivate and reactivate actions.',
  },
  {
    key: 'configuration',
    label: 'Configuration',
    path: '/configuration',
    icon: Settings,
    group: 'MANAGEMENT',
    deliveredBy: 'Feature f2.1',
    summary:
      'The Admin-only settings area, grouped into sections so later modules add sections without a schema change.',
  },
  {
    key: 'reference-data',
    label: 'Reference data',
    path: '/reference-data',
    icon: Library,
    group: 'MANAGEMENT',
    deliveredBy: 'Feature f2.2',
    summary:
      'The Admin-only lookup tables, countries and their aliases, and the Resource BU mapping that turns a feed spelling into a canonical PSU.',
  },
] as const;

/** Groups in the order the sidebar renders them. */
export const NAV_GROUP_ORDER: readonly NavGroup[] = [
  null,
  'ORGANIZATION',
  'COMMITMENT',
  'MANAGEMENT',
  'MONITOR',
] as const;

export function entriesInGroup(group: NavGroup): readonly NavEntry[] {
  return NAV_ENTRIES.filter((entry) => entry.group === group);
}

export function entryForPath(pathname: string): NavEntry | undefined {
  return NAV_ENTRIES.find(
    (entry) => pathname === entry.path || pathname.startsWith(`${entry.path}/`),
  );
}

/** Where "/" sends the user. */
export const DEFAULT_ROUTE = '/dashboard';
